import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CardRowDetail, CardRowDetailColumn, CardRowDetailLine, CardRowDetailLineSpacer } from "widgets";

import { SEQUENCE_TYPE_LABEL } from "../../../settings.ts";
import type { FeatureInfo, TrackType } from "../../../types.ts";
import { formatRegion } from "../../../utils/region.ts";
import { getEntityTypeFromTrackType } from "../../../utils/type-conversion.ts";
import { getSynonymString } from "../utils/get-synonym-string.ts";

export const TrackFeatureTooltip = ({
  info,
  currentTrackType,
  contigName,
}: {
  info: FeatureInfo;
  currentTrackType: TrackType | undefined;
  contigName: string | undefined;
}) => {
  const synonymString = useMemo(() => (info ? getSynonymString(info.synonyms) : ""), [info]);

  return (
    <CardRowDetail>
      <CardRowDetailColumn>
        <CardRowDetailLine title="Name">
          {!!currentTrackType && (
            <Link
              to={`/${getEntityTypeFromTrackType(currentTrackType)}/${info?.protein?.id ?? info?.gene?.id}`}
              target="_blank"
            >
              <div style={{ wordWrap: "break-word", textWrap: "wrap", overflow: "hidden", maxWidth: 600 }}>
                {info.name}
              </div>
            </Link>
          )}
        </CardRowDetailLine>
        {!!info?.description && (
          <CardRowDetailLine title="Description" wrap>
            {info?.description}
          </CardRowDetailLine>
        )}
        {!!info?.synonyms && info.synonyms?.length > 0 && (
          <CardRowDetailLine title={"Synonyms"} key={"synonyms"} wrap>
            {synonymString}
          </CardRowDetailLine>
        )}
        {!!info?.sequenceTypeId && (
          <CardRowDetailLine title={"Sequence Type"} key={"seqType"} wrap>
            {SEQUENCE_TYPE_LABEL[info.sequenceTypeId]}
          </CardRowDetailLine>
        )}
        {!!info?.status && (
          <CardRowDetailLine title={"Status"} key={"status"} wrap>
            {info.status}
          </CardRowDetailLine>
        )}
        <CardRowDetailLineSpacer />
        <CardRowDetailLine title="Region">
          {!!info.start && !!info.stop && formatRegion(contigName ?? info.name, info.start, info.stop)}
        </CardRowDetailLine>
        <CardRowDetailLine title="Length">
          {!!info.start && !!info.stop && `${(info.stop - info.start + 1).toLocaleString("en-US")} bp`}
        </CardRowDetailLine>
        <CardRowDetailLine title="Strand">{info.strand}</CardRowDetailLine>
        {!!info?.extReferences && info.extReferences.length > 0 && (
          <CardRowDetailLine title={"External References"} wrap>
            {info.extReferences.map((e, i) => (
              <div key={`feature-tooltips-row${i}`}>
                <a target="_blank" rel="noopener noreferrer" title={`Open link in new tab`} href={e.link}>
                  {e.displayName}
                </a>
              </div>
            ))}
          </CardRowDetailLine>
        )}
      </CardRowDetailColumn>
    </CardRowDetail>
  );
};
